/**
 * Story 1.9 + fix Story 1.9.1 — Hook que detecta bundle JS stale.
 *
 * BUG ORIGINAL (Story 1.9):
 * Usuária com bundle JS antigo em cache via menu lateral com strings
 * placeholder. Hard refresh não resolveu. Browser ignorava Cache-Control.
 *
 * FIX ORIGINAL: Comparava git SHA do bundle (`__APP_VERSION__` injetado
 * pelo Vite no build) com SHA do backend (`/api/version`). Se diferentes,
 * forçava reload.
 *
 * BUG DO FIX (que motivou Story 1.9.1):
 * Comparar SHA frontend vs SHA backend cria LOOP quando há deploy parcial:
 *   - Frontend buildado no commit X (bundle tem __APP_VERSION__ = X)
 *   - Backend deployado no commit Y > X (sem alterar nada de frontend)
 *   - Hook chama /api/version → retorna Y
 *   - Compara X !== Y → modal "atualize" → reload
 *   - Browser recarrega, baixa MESMO bundle (nginx serve mesmo arquivo X)
 *   - LOOP infinito.
 *
 * FIX DEFINITIVO (Story 1.9.1):
 * Comparar **bundle hash do <script> tag** (vindo do HTML do nginx) com
 * o bundle hash **atualmente carregado no DOM**. Não envolve backend —
 * é frontend vs frontend, mesma origem.
 *
 *   - Bundle hash muda APENAS quando o Vite re-buildou o frontend
 *   - Se nginx serve mesmo bundle → currentHash === serverHash → SEM modal
 *   - Se Deploy Frontend gera bundle novo → currentHash != serverHash → modal correto
 *   - Deploy só do backend não afeta — comportamento esperado
 *
 * EDGE CASES (preservados):
 *   - Bundle hash não encontrado no DOM → ignora (página customizada)
 *   - Bundle hash não encontrado no HTML → ignora (rota inválida)
 *   - Erro de rede → silencioso
 *   - Modo dev (sem build do Vite) → ignora
 */
import { useEffect, useState } from 'react';
import { APP_VERSION } from '../config/version';

export interface VersionCheckState {
  isStale: boolean;
  serverVersion: string | null;
  clientVersion: string;
  lastCheckedAt: number | null;
}

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos

/**
 * Extrai o hash do bundle JS atualmente carregado no documento.
 * O Vite gera URLs do tipo `/assets/index-XXXXXXXX.js` onde XXXXXXXX
 * é hash do conteúdo (content-based, muda quando código muda).
 */
function getCurrentBundleHash(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src*="/assets/index-"]'),
  );
  for (const s of scripts) {
    const match = s.src.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/);
    if (match) return match[1];
  }
  return null;
}

/**
 * Busca o HTML do `/cloud/` e extrai o hash do bundle JS que o nginx
 * está SERVINDO no momento. Se Deploy Frontend gerou bundle novo, o
 * HTML novo aponta pro hash novo.
 */
async function fetchServerBundleHash(): Promise<string | null> {
  try {
    const r = await fetch('/cloud/', {
      method: 'GET',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!r.ok) return null;
    const html = await r.text();
    const match = html.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(options?: {
  onStaleDetected?: () => void;
  pollIntervalMs?: number;
}): VersionCheckState {
  const [state, setState] = useState<VersionCheckState>({
    isStale: false,
    serverVersion: null,
    clientVersion: APP_VERSION,
    lastCheckedAt: null,
  });

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const currentBundle = getCurrentBundleHash();
        const serverBundle = await fetchServerBundleHash();
        if (cancelled) return;

        // Sem bundle hash em uma das pontas: skip silently
        if (!currentBundle || !serverBundle) return;

        const isStale = currentBundle !== serverBundle;

        setState({
          isStale,
          serverVersion: serverBundle,
          clientVersion: currentBundle,
          lastCheckedAt: Date.now(),
        });

        if (isStale) {
          // eslint-disable-next-line no-console
          console.warn(
            '[VersionCheck] Bundle hash stale — server=%s client=%s. Modal sera exibido.',
            serverBundle,
            currentBundle,
          );
          options?.onStaleDetected?.();
        }
      } catch {
        // Silencioso — falha de rede nao deve bloquear o app.
      }
    };

    // Check imediato no mount + polling.
    check();
    const intervalMs = options?.pollIntervalMs ?? CHECK_INTERVAL_MS;
    const timer = setInterval(check, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

/**
 * Força reload do bundle. Limpa caches e usa `location.reload(true)`
 * (truthy parameter — embora deprecated, alguns browsers respeitam).
 */
export async function forceReloadApp(): Promise<void> {
  try {
    // Tenta limpar Service Worker se houver
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    // Limpa Cache Storage (PWA)
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_e) {
    // Best-effort: continua para reload mesmo se limpeza falhar.
  }
  // Adiciona query string anti-cache + reload completo
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  window.location.replace(url.toString());
}
